'use client';

import React from 'react';

import { ChevronRight20Filled } from '@fluentui/react-icons';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

const accordionVariants = tv({
  base: 'inline-flex justify-start items-center',
  variants: {
    variant: {
      default: 'text-gray-500',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type AdditionalSharedProps = {
  label: string;
  counter?: string;
  counterType?: 'number' | 'icon';
  counterSize?: 'sm' | 'md';
};

export type AccordionTriggerProps = AccordionPrimitive.AccordionTriggerProps &
  VariantProps<typeof accordionVariants> &
  AdditionalSharedProps;

export const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>((props, ref) => {
  return (
    <AccordionPrimitive.Trigger
      className={cn(
        'flex w-full items-center justify-start gap-2 transition-all [&[data-state=open]>svg]:rotate-90',
        accordionVariants({
          variant: props.variant,
          className: props.className,
        }),
      )}
      ref={ref}
      {...props}
    >
      <span className="whitespace-nowrap font-normal text-gray-900">
        {props.label}
      </span>
      <hr className="flex h-[0.063rem] w-full flex-grow border-none bg-gray-300" />
      <ChevronRight20Filled className="h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  );
});

AccordionTrigger.displayName = 'AccordionTrigger';

export const DEFAULT_ACCORDIAN_ITEM = 'item-1';

export type AccordionProps = AccordionPrimitive.AccordionImplSingleProps &
  VariantProps<typeof accordionVariants> &
  AdditionalSharedProps;
export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <AccordionPrimitive.Root
        className={cn(
          accordionVariants({
            variant,
            className,
          }),
        )}
        collapsible
        ref={ref}
        type="single"
        {...props}
      >
        <AccordionPrimitive.AccordionItem
          className="w-full space-y-3"
          value={DEFAULT_ACCORDIAN_ITEM}
        >
          <AccordionTrigger
            counter={props.counter}
            counterSize={props.counterSize}
            counterType={props.counterType}
            label={props.label}
          />
          <AccordionPrimitive.AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm transition-all">
            {props.children}
          </AccordionPrimitive.AccordionContent>
        </AccordionPrimitive.AccordionItem>
      </AccordionPrimitive.Root>
    );
  },
);

Accordion.displayName = 'Accordion';
